import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { t } from "i18next";
import { toast } from "react-toastify";
import { useLoading } from "context/LoadingContext";
import { URL_PREFIX } from "constants";
import { STORAGE } from "constants/common";
import { getCookie } from "utils";
import { useExportStore } from "store/exportStore";

const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Start export process - returns export_id
const startExport = async (payload) => {
  const accessToken = getCookie(STORAGE.ACCESS_TOKEN);
  const url = `${process.env.REACT_APP_API_URL}${URL_PREFIX}export-employee`;

  const response = await axios.post(url, payload, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    validateStatus: (status) => status >= 200 && status < 500,
  });

  if (response.status === 200 && response.data?.status && response.data?.result?.data?.export_id) {
    return response.data.result.data.export_id;
  }

  throw new Error(response.data?.message || "Failed to start export");
};

// Check export status
export const checkExportStatus = async (exportId) => {
  const accessToken = getCookie(STORAGE.ACCESS_TOKEN);
  const url = `${process.env.REACT_APP_API_URL}${URL_PREFIX}export-employee/status/${exportId}`;

  const response = await axios.get(url, {
    headers: {
      Accept: "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    validateStatus: (status) => status >= 200 && status < 500,
  });

  if (response.status === 200 && response.data?.status && response.data?.result?.data) {
    return response.data.result.data;
  }

  throw new Error(response.data?.message || "Failed to check export status");
};

// Download export file
export const downloadExportFile = async (exportId, filename) => {
  const accessToken = getCookie(STORAGE.ACCESS_TOKEN);
  const url = `${process.env.REACT_APP_API_URL}${URL_PREFIX}export-employee/download/${exportId}`;

  const response = await axios.get(url, {
    responseType: "blob",
    headers: {
      Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    validateStatus: (status) => status >= 200 && status < 500,
  });

  if (response.status === 200) {
    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    
    const finalFilename = filename || "研修計画.xlsx";
    downloadFile(blob, finalFilename);
    return true;
  }

  throw new Error("Failed to download file");
};

export const useExportEmployee = () => {
  const { withLoading } = useLoading();
  const { addExport } = useExportStore();

  const startExportMutation = useMutation(
    (payload) => withLoading(() => startExport(payload)),
    {
      onSuccess: (exportId, variables) => {
        // Add to store for progress tracking
        const years = variables.years || [];
        const yearsString = years.join("、");
        const filename = `${yearsString ? `${yearsString}年度_` : ""}研修計画.xlsx`;

        addExport({
          exportId,
          status: "processing",
          employeeIds: variables.employee_ids || [],
          years: variables.years || [],
          filename,
          createdAt: new Date().toISOString(),
        });
      },
      onError: (error) => {
        console.error("Export start error:", error);
        toast.error(error.message || t("pages.messages.error"));
      },
    }
  );

  return {
    mutateAsync: startExportMutation.mutateAsync,
    checkExportStatus,
    downloadExport: downloadExportFile,
  };
};
